import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ShoppingCart, Search, Download } from 'lucide-react'
import { useOrdersList } from '../../../hooks/data/useOrders'
import { useUsers } from '@/hooks/data/useUsers'
import { downloadCsv } from '@/lib/csv'
import type { OrderListFilters, OrderStatus, OrderSummary } from '@/types/ecommerce'

function statusColor(s: string) {
  return (
    {
      Pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      Processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      Completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      Refunded: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    }[s] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  )
}

function money(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

function OrdersPage() {
  const navigate = useNavigate()
  // Deep-link from the ecommerce analytics dashboard's status donut (?status=Pending etc.) —
  // same raw URLSearchParams read as messages/page.tsx's ?conversationId=, since this route
  // doesn't have a validateSearch schema either.
  const [status, setStatus] = useState<OrderStatus | ''>(
    () => (new URLSearchParams(window.location.search).get('status') as OrderStatus | null) ?? '',
  )
  const [customerId, setCustomerId] = useState('')
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const [lookupId, setLookupId] = useState('')
  const cursor = cursorStack[cursorStack.length - 1]
  const customers = useUsers('customer')

  // Sorted by name so the dropdown reads like a picker, not a dump of accounts —
  // same "pick a real person, not a pasted GUID" idea as Messages' inbox list.
  const customerOptions = useMemo(
    () =>
      [...(customers.data ?? [])].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [customers.data],
  )

  const filters: OrderListFilters = {
    status: status || undefined,
    customerId: customerId || undefined,
    cursor,
    pageSize: 20,
  }
  const list = useOrdersList(filters)

  const resetPaging = () => setCursorStack([])

  const jumpToOrder = () => {
    const id = lookupId.trim()
    if (id) navigate({ to: '/orders/$id', params: { id } })
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" /> Orders
        </h1>
        <button
          onClick={() =>
            downloadCsv(
              `orders-page-${new Date().toISOString().slice(0, 10)}.csv`,
              (list.data?.items ?? []).map((o) => ({
                orderNumber: o.orderNumber,
                status: o.status,
                paymentStatus: o.paymentStatus,
                total: o.total,
                createdAt: o.createdAt,
              })),
            )
          }
          disabled={!list.data?.items.length}
          title="Exports this filtered page only — not every order matching these filters"
          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Export page
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <input
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && jumpToOrder()}
            placeholder="Open order by ID…"
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm w-64"
          />
          <button
            onClick={jumpToOrder}
            disabled={!lookupId.trim()}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-40"
          >
            <Search className="w-4 h-4" /> Open
          </button>
        </div>

        <select
          value={customerId}
          onChange={(e) => {
            setCustomerId(e.target.value)
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm w-64"
        >
          <option value="">All customers</option>
          {customerOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.email}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as OrderStatus | '')
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Refunded">Refunded</option>
        </select>
      </div>

      {list.isLoading ? (
        <p className="p-6 text-gray-500">Loading orders…</p>
      ) : list.isError ? (
        <p className="p-6 text-red-600">Failed to load orders. Requires a logged-in session.</p>
      ) : list.data && list.data.items.length === 0 ? (
        <p className="p-6 text-gray-500">No orders match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 font-medium">Order #</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Payment</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((o: OrderSummary) => (
                <tr key={o.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate({ to: '/orders/$id', params: { id: o.id } })}
                      className="text-blue-600 hover:underline"
                    >
                      {o.orderNumber}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(o.status)}`}>{o.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{o.paymentStatus}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{money(o.total)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{new Date(o.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3">
        {cursorStack.length > 0 && (
          <button onClick={() => setCursorStack((prev) => prev.slice(0, -1))} className="text-sm text-blue-600 hover:underline">
            ← Newer
          </button>
        )}
        {list.data?.hasMore && list.data.nextCursor && (
          <button
            onClick={() => setCursorStack((prev) => [...prev, list.data!.nextCursor!])}
            className="text-sm text-blue-600 hover:underline"
          >
            Older →
          </button>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/orders/')({ component: OrdersPage })
